import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { QuestionnaireAnswer, QuestionInPage } from 'src/app/core/models/questionnaire.model';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { runConditionsArray, runConditions, getValue } from '../../../../utils/hideif';

export interface EligibilityInfo {
  isIneligible: boolean;
  violatedRules: ViolatedRule[];
  affectedQuestions: Set<string>;
}

export interface ViolatedRule {
  ruleIndex: number;
  rule: any;
  matchedConditions: MatchedCondition[];
  errorMessage: string | null;      // Para exibição inline
  ruleMessage?: string | null;      // Backend usa para mensagem do modal
  rulePriority?: number;            // Prioridade da mensagem da regra
}

export interface MatchedCondition {
  key: string;                    // Frontend usa 'key'
  questionKey?: string;           // Backend usa 'questionKey' (alias)
  condition: string;              // Frontend
  operator?: string;              // Backend usa 'operator' (alias)
  expectedValue: any;
  actualValue: any;
  errorMessage: string | null;    // Para exibição inline
  customMessage?: string | null;  // Backend usa para mensagem do modal
  hideErrorMessage?: boolean;
  priority?: number;              // Prioridade da mensagem (backend)
}

/**
 * Serviço central para gerenciar validação de elegibilidade de claims
 * baseado em eligibility_questionnaire_rules do cp_case
 */
@Injectable()
export class EligibilityService {
  private eligibilityRules: any[] = [];
  private questionsMap: Record<string, QuestionInPage> = {};
  private questionnaireId: string = '';

  private ineligibilityInfo$ = new BehaviorSubject<EligibilityInfo>({
    isIneligible: false,
    violatedRules: [],
    affectedQuestions: new Set()
  });

  constructor(private claimsService: ClaimsService) {}

  /**
   * Inicializa o serviço com as regras de elegibilidade do case
   */
  async initialize(
    claimId: string,
    questionnaireId: string,
    questionsMap: Record<string, QuestionInPage>
  ): Promise<void> {
    this.questionnaireId = questionnaireId;
    this.questionsMap = questionsMap;

    try {
      const claimWithCase = await this.claimsService.getClaimWithCase(claimId);

      if (!claimWithCase?.cp_case?.configuration) {
        console.log('[ELIGIBILITY_SERVICE] No case configuration found');
        this.eligibilityRules = [];
        return;
      }

      const config = claimWithCase.cp_case.configuration as any;
      this.eligibilityRules = config.eligibility_questionnaire_rules || [];
    } catch (error) {
      console.error('[ELIGIBILITY_SERVICE] Error loading eligibility rules:', error);
      this.eligibilityRules = [];
    }
  }

  /**
   * Avalia se as respostas atuais violam alguma regra de elegibilidade
   * IMPORTANTE: Filtra apenas regras que referenciam questões DESTE questionário
   */
  evaluate(questionnaireAnswer: QuestionnaireAnswer | null): void {
    if (!this.eligibilityRules || this.eligibilityRules.length === 0) {
      this.ineligibilityInfo$.next({
        isIneligible: false,
        violatedRules: [],
        affectedQuestions: new Set()
      });
      return;
    }

    if (!questionnaireAnswer) {
      return;
    }

    // Obter lista de question slugs DESTE questionário
    const questionsInThisQuestionnaire = new Set<string>();
    Object.values(this.questionsMap).forEach(q => {
      if (q.cp_question?.slug) {
        questionsInThisQuestionnaire.add(q.cp_question.slug);
      }
    });

    const data = { answers: questionnaireAnswer.answers || {} };
    const violations: ViolatedRule[] = [];
    const affectedQuestions = new Set<string>();

    // Testar cada eligibility rule
    for (let i = 0; i < this.eligibilityRules.length; i++) {
      const rule = this.eligibilityRules[i];

      // Extrair todas as condições da regra
      let allConditions: any[] = [];
      if (Array.isArray(rule)) {
        for (const conditioner of rule) {
          if (conditioner.conditions) {
            allConditions.push(...conditioner.conditions);
          }
        }
      } else if (rule.conditions) {
        allConditions = rule.conditions;
      }

      // Verificar se TODAS as questões desta regra estão NESTE questionário
      const questionSlugsInRule = new Set<string>();
      allConditions.forEach(condition => {
        const keyParts = condition.key.split('.');
        if (keyParts[0] === 'answers' && keyParts.length >= 2) {
          questionSlugsInRule.add(keyParts[1]);
        }
      });

      // Verificar se HÁ PELO MENOS UMA questão da regra NESTE questionário
      // (não precisa ter todas, pois pode haver regras multi-questionário)
      const hasAnyQuestionInThisQuestionnaire = Array.from(questionSlugsInRule).some(
        slug => questionsInThisQuestionnaire.has(slug)
      );

      // SKIP regras que NÃO referenciam NENHUMA questão deste questionário
      if (!hasAnyQuestionInThisQuestionnaire) {
        continue;
      }

      try {
        // runConditionsArray espera array
        const ruleArray = Array.isArray(rule) ? rule : [rule];
        const matched = runConditionsArray(ruleArray, data);

        if (matched) {
          // Regra violada - coletar informações
          const matchedConditions: MatchedCondition[] = [];

          // errorMessage no nível do aggregator (fallback global da regra)
          const ruleErrorMessage = rule.errorMessage || null;

          // Determinar quais questões REALMENTE causaram a violação
          // Isso depende do aggregator da regra
          const aggregator = Array.isArray(rule) ? 'all' : rule.aggregator;

          // Para cada condição, verificar se ela individualmente viola
          for (const condition of allConditions) {
            const actualValue = getValue(data, condition.key);

            // Verificar se ESTA condição especificamente viola
            const conditionViolated = runConditions({
              aggregator: 'all',
              conditions: [condition]
            }, data);

            // Extrair question slug do key (ex: "answers.cpf_leak_check" -> "cpf_leak_check")
            const keyParts = condition.key.split('.');
            const questionSlug = keyParts[0] === 'answers' && keyParts.length >= 2 ? keyParts[1] : null;

            // Adicionar às affected questions apenas se:
            // 1. aggregator = 'all' → todas as condições violam (adicionar todas)
            // 2. aggregator = 'any' → apenas as que violam individualmente
            // 3. aggregator = 'none' → apenas as que violam individualmente (inverso)
            if (questionSlug) {
              if (aggregator === 'all') {
                // No 'all', todas as condições são necessárias
                affectedQuestions.add(questionSlug);
              } else if (aggregator === 'any' && conditionViolated) {
                // No 'any', apenas as que violam individualmente
                affectedQuestions.add(questionSlug);
              } else if (aggregator === 'none' && conditionViolated) {
                // No 'none', apenas as que violam individualmente
                affectedQuestions.add(questionSlug);
              }
            }

            // Prioridade: errorMessage da condição individual > errorMessage da regra
            const conditionErrorMessage = condition.errorMessage || ruleErrorMessage;

            matchedConditions.push({
              key: condition.key,
              questionKey: condition.key,                                    // Alias backend
              condition: condition.condition,
              operator: condition.condition,                                // Alias backend
              expectedValue: condition.value,
              actualValue,
              errorMessage: conditionErrorMessage,
              customMessage: condition.claimInactivateMessage || null,      // Para modal
              hideErrorMessage: condition.hideErrorMessage || false,
              priority: condition.claimInactivateMessagePriority ?? 0       // Prioridade backend
            });
          }

          violations.push({
            ruleIndex: i,
            rule: rule,
            matchedConditions,
            errorMessage: ruleErrorMessage,
            ruleMessage: rule.claimInactivateMessage || null,           // Para modal
            rulePriority: rule.claimInactivateMessagePriority ?? 0      // Prioridade
          });
        }
      } catch (evalError) {
        console.error('[ELIGIBILITY_SERVICE] Error evaluating rule:', {
          ruleIndex: i,
          error: evalError instanceof Error ? evalError.message : String(evalError)
        });
      }
    }

    const previousInfo = this.ineligibilityInfo$.value;
    const newInfo: EligibilityInfo = {
      isIneligible: violations.length > 0,
      violatedRules: violations,
      affectedQuestions
    };

    this.ineligibilityInfo$.next(newInfo);
  }

  /**
   * Obtém o observable de informações de inelegibilidade
   */
  getIneligibilityInfo$(): Observable<EligibilityInfo> {
    return this.ineligibilityInfo$.asObservable();
  }

  /**
   * Obtém o valor atual de informações de inelegibilidade
   */
  getIneligibilityInfo(): EligibilityInfo {
    return this.ineligibilityInfo$.value;
  }

  /**
   * Verifica se uma questão específica está causando inelegibilidade
   * Retorna a mensagem de erro mais específica disponível
   */
  getQuestionIneligibilityMessage(questionSlug: string): string | null {
    const info = this.ineligibilityInfo$.value;

    if (!info.isIneligible) {
      return null;
    }

    // Verifica se esta questão específica está nas affected questions
    if (!info.affectedQuestions.has(questionSlug)) {
      return null;
    }

    // Buscar mensagem de erro customizada da regra/condição violada
    // Prioridade: errorMessage da condição individual > errorMessage da regra > mensagem genérica
    for (const violation of info.violatedRules) {
      for (const cond of violation.matchedConditions) {
        const keyParts = cond.key.split('.');
        if (keyParts[0] === 'answers' && keyParts[1] === questionSlug) {
          // Usar errorMessage da condição se existir, senão da regra
          return cond.errorMessage || violation.errorMessage ||
            'This answer may affect your eligibility for this claim.';
        }
      }
    }

    return 'This answer may affect your eligibility for this claim.';
  }

  /**
   * Retorna a mensagem customizada de inativação de claim
   * Prioridade: claimInactivateMessage da condição > claimInactivateMessage da regra > null
   * Usado pelo InactiveClaimModal para exibir mensagem específica do case
   */
  getClaimInactivateMessage(): string | null {
    const info = this.ineligibilityInfo$.value;

    if (!info.isIneligible || info.violatedRules.length === 0) {
      return null;
    }

    // Buscar mensagem com maior prioridade
    // Prioridade: condition-level > rule-level, depois compara priority número
    let bestMessage: string | null = null;
    let bestPriority = -1;
    let bestLevel: 'condition' | 'rule' | null = null;

    for (const violation of info.violatedRules) {
      // Primeiro, verifica condições (maior prioridade)
      for (const cond of violation.matchedConditions) {
        if (cond.customMessage) {
          const priority = cond.priority ?? 0;

          if (bestLevel !== 'condition' || priority > bestPriority) {
            bestMessage = cond.customMessage;
            bestPriority = priority;
            bestLevel = 'condition';
          }
        }
      }

      // Se não achou condition-level, verifica rule-level
      if (!bestLevel && violation.ruleMessage) {
        const priority = violation.rulePriority ?? 0;

        if (priority > bestPriority) {
          bestMessage = violation.ruleMessage;
          bestPriority = priority;
          bestLevel = 'rule';
        }
      }
    }

    if (bestMessage) {
      return bestMessage;
    }

    return null;
  }

  /**
   * Reseta o estado do serviço
   */
  reset(): void {
    this.eligibilityRules = [];
    this.questionsMap = {};
    this.questionnaireId = '';
    this.ineligibilityInfo$.next({
      isIneligible: false,
      violatedRules: [],
      affectedQuestions: new Set()
    });
  }
}
